import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { 
  Stethoscope, 
  CheckCircle, 
  Clock, 
  AlertTriangle,
  TrendingUp,
  Plus,
  RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../../App';
import { format } from 'date-fns';

const MemberServiceUsageDialog = ({ 
  member, 
  isOpen, 
  onClose,
  onRecordUsage 
}) => {
  const [usageSummary, setUsageSummary] = useState([]);
  const [usageHistory, setUsageHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recordingService, setRecordingService] = useState(null);

  useEffect(() => {
    if (isOpen && member?.id) {
      fetchUsageData();
    }
  }, [isOpen, member?.id]);

  const fetchUsageData = async () => {
    setLoading(true);
    try {
      const [summaryRes, historyRes] = await Promise.all([
        axios.get(`${API}/clinic/services/usage/member/${member.id}`).catch(() => ({ data: [] })),
        axios.get(`${API}/clinic/services/usage/history/${member.id}`).catch(() => ({ data: [] }))
      ]);
      setUsageSummary(summaryRes.data || []);
      setUsageHistory(historyRes.data || []);
    } catch (error) {
      console.error('Error fetching usage data:', error);
      setUsageSummary([]);
      setUsageHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRecordUsage = async (serviceId) => {
    setRecordingService(serviceId);
    try {
      const response = await axios.post(`${API}/clinic/services/usage`, {
        member_id: member.id,
        service_id: serviceId
      });
      
      if (response.data.success) {
        toast.success('Service usage recorded successfully');
        fetchUsageData();
      } else {
        toast.error(response.data.message || 'Failed to record usage');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record service usage');
    } finally {
      setRecordingService(null);
    }
  };

  const getUsagePercentage = (used, allowed) => {
    if (allowed === -1) return 0;
    if (allowed === 0) return 100;
    return Math.min((used / allowed) * 100, 100);
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 70) return 'bg-yellow-500';
    return 'bg-teal-500';
  };

  const totalUsed = usageSummary.reduce((acc, s) => acc + s.quantity_used, 0);
  const totalRemaining = usageSummary.reduce((acc, s) => {
    if (s.quantity_remaining === -1) return acc;
    return acc + s.quantity_remaining;
  }, 0);
  const hasUnlimited = usageSummary.some(s => s.quantity_remaining === -1);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Stethoscope className="h-5 w-5 text-teal-600" />
            Service Usage - {member?.patient_id}
          </DialogTitle>
          <DialogDescription>
            Track and record service usage for {member?.user?.full_name || 'this member'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500">Available Services</p>
                      <p className="text-xl font-bold text-gray-900">{usageSummary.length}</p>
                    </div>
                    <Stethoscope className="h-6 w-6 text-teal-600" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500">Services Used</p>
                      <p className="text-xl font-bold text-blue-600">{totalUsed}</p>
                    </div>
                    <CheckCircle className="h-6 w-6 text-blue-600" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500">Remaining</p>
                      <p className="text-xl font-bold text-green-600">
                        {hasUnlimited ? '∞' : totalRemaining}
                      </p>
                    </div>
                    <TrendingUp className="h-6 w-6 text-green-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Services List */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-gray-900">Plan Services</h4>
                <Button variant="outline" size="sm" onClick={fetchUsageData}>
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Refresh
                </Button>
              </div>

              {usageSummary.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <Stethoscope className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No services assigned to this member&apos;s plan</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Assign services to the membership plan first
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {usageSummary.map((service) => {
                    const percentage = getUsagePercentage(service.quantity_used, service.quantity_allowed);
                    const isUnlimited = service.quantity_allowed === -1;
                    const isLow = !isUnlimited && service.quantity_remaining <= 1 && service.quantity_remaining > 0;
                    const isExhausted = !isUnlimited && service.quantity_remaining <= 0;

                    return (
                      <div 
                        key={service.service_id} 
                        className="p-4 border rounded-lg hover:shadow-sm transition-shadow bg-white"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h5 className="font-medium text-gray-900">{service.service_name}</h5>
                            <p className="text-xs text-gray-500">
                              Category: {service.category} • Period: {service.period_type}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {isUnlimited ? (
                              <Badge className="bg-green-100 text-green-800">Unlimited</Badge>
                            ) : isExhausted ? (
                              <Badge className="bg-red-100 text-red-800">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                Exhausted
                              </Badge>
                            ) : isLow ? (
                              <Badge className="bg-yellow-100 text-yellow-800">
                                <Clock className="h-3 w-3 mr-1" />
                                Low
                              </Badge>
                            ) : (
                              <Badge className="bg-teal-100 text-teal-800">Available</Badge>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center justify-between gap-4">
                          <div className="flex-1">
                            {!isUnlimited && (
                              <div className="space-y-1">
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">
                                    Used: <strong>{service.quantity_used}</strong> of {service.quantity_allowed}
                                  </span>
                                  <span className="font-medium text-gray-900">
                                    Remaining: <strong>{service.quantity_remaining}</strong>
                                  </span>
                                </div>
                                <Progress value={percentage} className={`h-2 ${getProgressColor(percentage)}`} />
                              </div>
                            )}
                            {isUnlimited && (
                              <p className="text-sm text-gray-600">
                                Total used: <strong>{service.quantity_used}</strong>
                              </p>
                            )}
                          </div>
                          
                          <Button
                            size="sm"
                            variant={isExhausted ? "outline" : "default"}
                            disabled={isExhausted || recordingService === service.service_id}
                            onClick={() => handleRecordUsage(service.service_id)}
                            className={!isExhausted ? "bg-teal-600 hover:bg-teal-700" : ""}
                          >
                            {recordingService === service.service_id ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <Plus className="h-4 w-4 mr-1" />
                                Record Use
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Usage History */}
            {usageHistory.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Recent Usage History</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {usageHistory.slice(0, 10).map((usage) => (
                    <div 
                      key={usage.id} 
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                          <CheckCircle className="h-4 w-4 text-teal-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{usage.service_name}</p>
                          <p className="text-xs text-gray-500">
                            {usage.usage_date ? format(new Date(usage.usage_date), 'PPP') : 'N/A'}
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {usage.service_category}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MemberServiceUsageDialog;
